import React, { useEffect, useRef, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";

type CopyButtonSize = "inline" | "xs" | "sm";
type CopyStatus = "idle" | "copied" | "error";

// Boxed sizes are the icon buttons' own, so a copy button sits level with its neighbours.
const BOXED = "rounded-app text-fg hover:bg-bg-hover hover:text-fg-muted";

const SIZES: Record<CopyButtonSize, { icon: number; className: string }> = {
  // The bare glyph in the surrounding text's colour, for a chip or a line of text.
  inline: { icon: 12, className: "shrink-0" },
  xs: { icon: 14, className: `action-icon-button-xs ${BOXED}` },
  sm: { icon: 16, className: `action-icon-button-sm ${BOXED}` },
};

export interface CopyButtonProps {
  /** The string, or a sync thunk for a truncated preview (WebKit rejects a write after an await). */
  text: string | (() => string);
  size?: CopyButtonSize;
  /** Defaults to "Copy to clipboard". */
  ariaLabel?: string;
  successDurationMs?: number;
  errorDurationMs?: number;
  /** Fired after a successful copy, e.g. to show a global notification. */
  onCopy?: () => void;
  className?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  size = "xs",
  ariaLabel = "Copy to clipboard",
  successDurationMs = 1200,
  errorDurationMs = 1500,
  onCopy,
  className = "",
}) => {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    try {
      const value = typeof text === "function" ? text() : text;
      await navigator.clipboard.writeText(value);
      setStatus("copied");
      onCopy?.();
      timeoutRef.current = setTimeout(
        () => setStatus("idle"),
        successDurationMs,
      );
    } catch {
      setStatus("error");
      timeoutRef.current = setTimeout(() => setStatus("idle"), errorDurationMs);
    }
  };

  const { icon: iconSize, className: sizeClassName } = SIZES[size];
  const icon =
    status === "copied" ? (
      <CheckIcon className="text-success" style={{ fontSize: iconSize }} />
    ) : status === "error" ? (
      <ErrorOutlineIcon className="text-error" style={{ fontSize: iconSize }} />
    ) : (
      <ContentCopyIcon style={{ fontSize: iconSize }} />
    );

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={ariaLabel}
      className={`inline-flex items-center justify-center cursor-pointer transition-colors ${sizeClassName} ${className}`}
    >
      {icon}
    </button>
  );
};
